import { CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  model,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { DspAdvertiserDeal, DspAdvertiserDealDealTypeEnum } from "@front/m19-api-client";
import { AuthService } from "@front/m19-services";
import {
  IAlertComponent,
  IDropdownMenuSeparatorComponent,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  ISelectMenuImports,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";
import { DspDealOptionComponent } from "./dsp-deal-option.component";
import { DspDealRefreshButtonComponent } from "@m19-board/dsp/dsp-media/dsp-deal-refresh-button.component";

const DEAL_TYPE_ORDER: DspAdvertiserDealDealTypeEnum[] = [
  DspAdvertiserDealDealTypeEnum.PRIVATE_AUCTION,
  DspAdvertiserDealDealTypeEnum.PREFERRED,
  DspAdvertiserDealDealTypeEnum.PROGRAMMATIC_GUARANTEED,
];

@Component({
  selector: "app-dsp-deal-selector",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./dsp-deal-selector.component.html",
  imports: [
    DspDealRefreshButtonComponent,
    CompactFilterComponent,
    CurrencyPipe,
    DspDealOptionComponent,
    IAlertComponent,
    IDropdownMenuSeparatorComponent,
    IFormFieldComponent,
    IInputDirective,
    ILabelComponent,
    ISelectMenuImports,
    TranslocoDirective,
    DecimalPipe,
  ],
  providers: [DatePipe],
})
export class DspDealSelectorComponent {
  private readonly authService = inject(AuthService);
  private readonly datePipe = inject(DatePipe);
  private readonly compactFilterService = inject(CompactFilterService);

  readonly deals = input.required<DspAdvertiserDeal[]>();
  readonly disabled = input<boolean>(false);
  readonly dealId = model<string | undefined>(undefined);
  readonly readonly = input<boolean>(false);

  readonly locale = toSignal(this.authService.userLocale$, { initialValue: "en-US" });

  readonly searchQuery = signal<string>("");

  readonly dealFilterOptions = computed<Filter<DealFilter>[]>(() => {
    const sources = [...new Set(this.deals().map((deal) => deal.source))];
    const types = DEAL_TYPE_ORDER.filter((dealType) => this.deals().some((deal) => deal.dealType === dealType));
    return [
      this.compactFilterService.getFilterWithOptions(FilterCategory.DspDealSource, sources ?? []),
      this.compactFilterService.getFilterWithOptions(FilterCategory.DspDealType, types ?? []),
    ];
  });

  readonly filters = linkedSignal<Filter<DealFilter>[], Filter<DealFilter>[]>({
    source: this.dealFilterOptions,
    computation: () => [],
  });
  readonly selectedDeal = computed(() => {
    const dealId = this.dealId();
    return dealId ? this.deals().find((deal) => deal.dealId === dealId) : undefined;
  });

  private readonly filteredDealsByType = computed(() => {
    const typeFilter = this.compactFilterService.findFilterWithType(this.filters(), FilterCategory.DspDealType);
    const sourceFilter = this.compactFilterService.findFilterWithType(this.filters(), FilterCategory.DspDealSource);
    const filteredDeals = this.deals().filter(
      (deal) =>
        (!typeFilter?.values.length || typeFilter.values.includes(deal.dealType)) &&
        (!sourceFilter?.values.length || sourceFilter.values.includes(deal.source)),
    );

    return DEAL_TYPE_ORDER.map((dealType) => ({
      dealType,
      deals: filteredDeals.filter((deal) => deal.dealType === dealType),
    })).filter((group) => group.deals.length > 0);
  });

  readonly dealsToDisplay = computed(() => {
    const searchQuery = this.searchQuery();
    if (!searchQuery) return this.filteredDealsByType();
    return this.filteredDealsByType()
      .map((group) => ({
        ...group,
        deals: group.deals.filter((deal) => deal.dealName.toLowerCase().includes(searchQuery.toLowerCase())),
      }))
      .filter((group) => group.deals.length > 0);
  });

  readonly enforcedFlightDeal = computed(() => {
    const deal = this.selectedDeal();
    return deal?.dealType === DspAdvertiserDealDealTypeEnum.PROGRAMMATIC_GUARANTEED &&
      deal.budget &&
      deal.startDate &&
      deal.endDate
      ? deal
      : undefined;
  });

  readonly guaranteedDateRangeLabel = computed(() => {
    const deal = this.enforcedFlightDeal();
    if (!deal) return "";
    const locale = this.locale();
    const start = this.datePipe.transform(deal.startDate, "shortDate", undefined, locale);
    const end = this.datePipe.transform(deal.endDate, "shortDate", undefined, locale);
    return `${start} – ${end}`;
  });

  readonly guaranteedMaxImpressions = computed(() => {
    const deal = this.enforcedFlightDeal();
    if (!deal?.budget || !deal.price) return undefined;
    return Math.floor((deal.budget / deal.price) * 1000);
  });

  selectDeal(dealId: string | undefined) {
    this.dealId.set(dealId);
  }

  protected applyFilter(filters: Filter<DealFilter>[]) {
    this.filters.set(filters);
  }
}

type DealFilter = FilterCategory.DspDealSource | FilterCategory.DspDealType;
